import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, ArrowUpRightIcon } from 'lucide-react';
import { HeroLogoMark } from '../components/HeroLogoMark';
import { ConsultantList } from '../components/ConsultantList';
import { TrustedByLeaders } from '../components/reviews/TrustedByLeaders';
import { isFeatureEnabled } from '../utils/features';
import { ButtonLink } from '../components/ButtonLink';
import { Reveal } from '../components/Reveal';
import { MethodSteps } from '../components/MethodSteps';
import { CtaBand } from '../components/CtaBand';
import { practiceAreas } from '../data/expertise';
import { industries } from '../data/industries';
import { insights } from '../data/insights';
import { formatDate } from '../utils/date';
export function Home() {
  const [featured, ...others] = insights;
  return <>
      <section className="relative overflow-hidden bg-midnight-950 text-champagne-50" data-id="element-462" data-mp="3kv0nr3ex6cry">
        <div className="mx-auto grid max-w-site items-center gap-10 px-6 pb-20 pt-14 lg:grid-cols-12 lg:px-10 lg:pb-28 lg:pt-20" data-id="element-463" data-mp="3ovare9f8or3n">
          <div className="lg:col-span-7" data-id="element-464" data-mp="30uf948u15x54">
            <p className="text-xs font-medium uppercase tracking-[0.28em] text-champagne-400" data-id="element-465" data-mp="2vxf13sdmekbd">Retained Executive Search</p>
            <h1 className="mt-6 font-serif text-5xl leading-[1.05] md:text-6xl lg:text-[4.25rem]" data-id="element-466" data-mp="2qx0tgbfxlcdv">Leadership that defines what comes next.</h1>
            <p className="mt-7 max-w-xl text-lg leading-relaxed text-champagne-100/75" data-id="element-467" data-mp="2hxxb59ol910f">
              Qorvessa Executive Partners is a retained executive search firm placing board members, chief executives, and senior
              leaders with organizations at moments of consequence.
            </p>
            <div className="mt-10 flex flex-wrap gap-4" data-id="element-468" data-mp="23912baepjjyr">
              <ButtonLink to="/contact?type=employer" withArrow data-id="element-469" data-mp="23alwv010v2wm">
                Discuss Your Hiring Needs
              </ButtonLink>
              <ButtonLink to="/contact?type=candidate" variant="outlineDark" data-id="element-470" data-mp="1u8cpuk4yprnz">
                Introduce Yourself
              </ButtonLink>
            </div>
          </div>
          <div className="lg:col-span-5" data-id="element-471" data-mp="1kjfk7hux1aof">
            <HeroLogoMark data-id="element-472" data-mp="1omu088iutr1v" />
          </div>
        </div>
      </section>

      <section className="bg-ivory" data-id="element-473" data-mp="1ojzgykbkuca1">
        <div className="mx-auto grid max-w-site gap-10 px-6 py-24 lg:grid-cols-12 lg:px-10 lg:py-32" data-id="element-474" data-mp="1ancjmgfppk3l">
          <Reveal className="lg:col-span-7" data-id="element-475" data-mp="1fkukdjjkst0i">
            <h2 className="font-serif text-4xl leading-tight text-midnight-900 md:text-5xl" data-id="element-476" data-mp="1fnqgr3frz9mu">
              We search for the leaders others overlook — and recommend only those we would stand behind.
            </h2>
          </Reveal>
          <Reveal delay={0.06} className="lg:col-span-4 lg:col-start-9 lg:pt-3" data-id="element-477" data-mp="1tlowac5u1c3x">
            <p className="text-[17px] leading-relaxed text-midnight-900/70" data-id="element-478" data-mp="1yki7tz1k6t1g">
              Every mandate is partner-led, research-driven, and handled with complete discretion. We work with a deliberately
              small number of clients so each search receives the senior attention it deserves.
            </p>
            <Link to="/about" className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-midnight-900 underline decoration-champagne-500 underline-offset-[6px]" data-id="element-479" data-mp="flk7z4glbd6u">
              About the firm
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-480" data-mp="oenj363rjdyb" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="bg-champagne-50" data-id="element-481" data-mp="kd2cu3lqhxx3">
        <div className="mx-auto max-w-site px-6 py-24 lg:px-10 lg:py-32" data-id="element-482" data-mp="3sscc73y8op16">
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between" data-id="element-483" data-mp="3sugc7at0p3w6">
            <h2 className="font-serif text-4xl text-midnight-900 md:text-5xl" data-id="element-484" data-mp="3nv3oyoy8hut2">Our expertise</h2>
            <Link to="/expertise" className="inline-flex items-center gap-2 text-sm font-medium text-midnight-900 underline decoration-champagne-500 underline-offset-[6px]" data-id="element-485" data-mp="5oxsyway3gcq">
              All practice areas
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-486" data-mp="1h4w6pdh2iqka" />
            </Link>
          </div>
          <ul className="mt-12 border-b border-midnight-900/15" data-id="element-487" data-mp="1c5t2twctqqdw">
            {practiceAreas.map(area => <li key={area.id} className="border-t border-midnight-900/15" data-id="element-488" data-mp="2cczr0cbe7p7f">
                <Link to={`/expertise#${area.id}`} className="group grid items-baseline gap-3 py-7 md:grid-cols-12 md:gap-8" data-id="element-489" data-mp="2cd81xynygkfz">
                  <h3 className="font-serif text-2xl text-midnight-900 transition-colors duration-150 ease-out group-hover:text-midnight-600 md:col-span-4 md:text-3xl" data-id="element-490" data-mp="2mb5s4rpdkrb8">
                    {area.title}
                  </h3>
                  <p className="text-[15px] leading-relaxed text-midnight-900/70 md:col-span-7" data-id="element-491" data-mp="2vb2aycp32vyd">{area.summary}</p>
                  <ArrowUpRightIcon className="hidden h-5 w-5 justify-self-end text-champagne-600 transition-transform duration-150 ease-out group-hover:-translate-y-0.5 group-hover:translate-x-0.5 md:col-span-1 md:block" aria-hidden="true" data-id="element-492" data-mp="303lq3e3f5z5a" />
                </Link>
              </li>)}
          </ul>
        </div>
      </section>

      <section className="bg-midnight-950 text-champagne-50" data-id="element-493" data-mp="3e08svxi1zjj4">
        <div className="mx-auto max-w-site px-6 py-24 lg:px-10 lg:py-32" data-id="element-494" data-mp="12frlowflroet">
          <div className="grid gap-8 lg:grid-cols-12" data-id="element-495" data-mp="ycv67eld08f3">
            <h2 className="font-serif text-4xl md:text-5xl lg:col-span-5" data-id="element-496" data-mp="yfhghlzmx210">Industries we serve</h2>
            <p className="text-[17px] leading-relaxed text-champagne-100/70 lg:col-span-5 lg:col-start-8" data-id="element-497" data-mp="tgea9y7x4prg">
              Deep sector networks paired with cross-industry perspective — so we can bring you leaders who know your market, and
              those who can transform it.
            </p>
          </div>
          <ul className="mt-14 grid gap-x-10 sm:grid-cols-2 lg:grid-cols-4" data-id="element-498" data-mp="1h68n4fs8mebp">
            {industries.map(industry => <li key={industry.id} className="border-t border-champagne-100/15 py-6" data-id="element-499" data-mp="17ekzyr2prjg1">
                <Link to={`/industries#${industry.id}`} className="font-serif text-2xl text-champagne-50 transition-colors duration-150 ease-out hover:text-champagne-300" data-id="element-500" data-mp="24t6y4xxl8u28">
                  {industry.name}
                </Link>
              </li>)}
          </ul>
        </div>
      </section>

      <section className="bg-ivory" data-id="element-501" data-mp="26jrxl3ukgrf">
        <div className="mx-auto max-w-site px-6 py-24 lg:px-10 lg:py-32" data-id="element-502" data-mp="25i6cmp7gbvg">
          <div className="max-w-2xl" data-id="element-503" data-mp="3klkmkpcqlqwn">
            <h2 className="font-serif text-4xl text-midnight-900 md:text-5xl" data-id="element-504" data-mp="3kkiy9j6pn7wz">A search methodology built on evidence</h2>
            <p className="mt-5 text-[17px] leading-relaxed text-midnight-900/70" data-id="element-505" data-mp="3phj3xszne8nk">
              Six disciplined stages take every mandate from first conversation to a successful first year in role.
            </p>
          </div>
          <div className="mt-14" data-id="element-506" data-mp="31mgepjwxm36k">
            <MethodSteps data-id="element-507" data-mp="31n894resluv9" />
          </div>
        </div>
      </section>

      {isFeatureEnabled('consultants') && <section className="bg-champagne-50" data-id="element-508" data-mp="36l9trgrva28u">
          <div className="mx-auto max-w-site px-6 py-24 lg:px-10 lg:py-32" data-id="element-509" data-mp="zu0nz1ci0j1g">
            <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between" data-id="element-510" data-mp="14tmyv44utn73">
              <h2 className="font-serif text-4xl text-midnight-900 md:text-5xl" data-id="element-511" data-mp="14rahas30deuk">Meet our consultants</h2>
              <Link to="/consultants" className="inline-flex items-center gap-2 text-sm font-medium text-midnight-900 underline decoration-champagne-500 underline-offset-[6px]" data-id="element-512" data-mp="19yh2nre8ru3o">
                Our team
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-513" data-mp="1iz6r2a16ai7d" />
              </Link>
            </div>
            <div className="mt-12" data-id="element-514" data-mp="1xnjshmj14t99">
              <ConsultantList data-id="element-515" data-mp="1xmryuh8zt5lm" />
            </div>
          </div>
        </section>}

      <section className="bg-ivory py-24 lg:py-32" data-id="element-516" data-mp="1snx5wmhba0ns">
        <div className="mx-auto grid max-w-site gap-6 px-6 lg:grid-cols-2 lg:px-10" data-id="element-517" data-mp="26ord51smhvkv">
          <Reveal className="flex h-full flex-col rounded-lg bg-midnight-900 p-10 text-champagne-50 lg:p-14" data-id="element-518" data-mp="26mn8uiqtauk2">
            <p className="text-sm font-medium text-champagne-300" data-id="element-519" data-mp="caqfulcq1gmw">For Employers</p>
            <h2 className="mt-4 font-serif text-4xl leading-tight" data-id="element-520" data-mp="cct53361v5qk">Build the leadership team your strategy requires.</h2>
            <p className="mt-5 text-[16px] leading-relaxed text-champagne-100/70" data-id="element-521" data-mp="wu32wmsq0g4p">
              Partner-led retained search for board, C-suite, and senior roles, with candid market intelligence at every stage.
            </p>
            <div className="mt-auto flex flex-wrap gap-4 pt-10" data-id="element-522" data-mp="16jmo3z3wom2q">
              <ButtonLink to="/contact?type=employer" withArrow data-id="element-523" data-mp="1algkhv72j3x3">
                Discuss Your Hiring Needs
              </ButtonLink>
              <ButtonLink to="/employers" variant="outlineDark" data-id="element-524" data-mp="dvz8kyhhslmy">
                How we partner
              </ButtonLink>
            </div>
          </Reveal>
          <Reveal delay={0.06} className="flex h-full flex-col rounded-lg bg-champagne-100 p-10 text-midnight-900 lg:p-14" data-id="element-525" data-mp="iw50mfroca5k">
            <p className="text-sm font-medium text-champagne-600" data-id="element-526" data-mp="rvi2ondgxwg9">For Candidates</p>
            <h2 className="mt-4 font-serif text-4xl leading-tight" data-id="element-527" data-mp="1yb2iwgfm9f41">Explore your next leadership chapter, in confidence.</h2>
            <p className="mt-5 text-[16px] leading-relaxed text-midnight-900/70" data-id="element-528" data-mp="239cg0wkwipsa">
              Many of our mandates are never advertised. Introduce yourself and we’ll be in touch when the right opportunity arises.
            </p>
            <div className="mt-auto flex flex-wrap gap-4 pt-10" data-id="element-529" data-mp="28fiwf35mik4o">
              <ButtonLink to="/contact?type=candidate" variant="solidDark" withArrow data-id="element-530" data-mp="23nhdwzhv3x67">
                Introduce Yourself
              </ButtonLink>
              <ButtonLink to="/opportunities" variant="outlineLight" data-id="element-531" data-mp="1zkl1xrmtuzb8">
                Leadership Opportunities
              </ButtonLink>
            </div>
          </Reveal>
        </div>
      </section>

      <TrustedByLeaders data-id="element-532" data-mp="1pqkon2he6nrf" />

      <section className="border-t border-midnight-900/10 bg-champagne-50" data-id="element-533" data-mp="1kp53laf7sh91">
        <div className="mx-auto max-w-site px-6 py-24 lg:px-10 lg:py-32" data-id="element-534" data-mp="1krhl5mh28plk">
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between" data-id="element-535" data-mp="1gpe6yq30b6wl">
            <h2 className="font-serif text-4xl text-midnight-900 md:text-5xl" data-id="element-536" data-mp="3f24mbcew5rkj">Insights</h2>
            <Link to="/insights" className="inline-flex items-center gap-2 text-sm font-medium text-midnight-900 underline decoration-champagne-500 underline-offset-[6px]" data-id="element-537" data-mp="3b35n88fucp10">
              All insights
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-538" data-mp="8m81tvbbddjk" />
            </Link>
          </div>
          <div className="mt-12 grid gap-10 lg:grid-cols-12" data-id="element-539" data-mp="mlg26rj7qrpc">
            <Reveal className="lg:col-span-7" data-id="element-540" data-mp="hn6a4pssc77y">
              <Link to={`/insights/${featured.slug}`} className="group flex h-full flex-col rounded-lg bg-midnight-900 p-10 text-champagne-50 lg:p-12" data-id="element-541" data-mp="hohccl6l16wy">
                <p className="text-sm text-champagne-300" data-id="element-542" data-mp="3l4wswjl4e9gx">
                  {featured.category} · {formatDate(featured.date)}
                </p>
                <h3 className="mt-6 font-serif text-4xl leading-tight transition-colors duration-150 ease-out group-hover:text-champagne-200" data-id="element-543" data-mp="3p7w5lidmcsz">
                  {featured.title}
                </h3>
                <p className="mt-5 max-w-lg text-[16px] leading-relaxed text-champagne-100/70" data-id="element-544" data-mp="37b5k5df7qtm9">{featured.excerpt}</p>
                <span className="mt-auto inline-flex items-center gap-2 pt-10 text-sm font-medium text-champagne-200" data-id="element-545" data-mp="32c2ogjl4hzqy">
                  Read article <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-0.5" aria-hidden="true" data-id="element-546" data-mp="1mn4j062rqw38" />
                </span>
              </Link>
            </Reveal>
            <ul className="lg:col-span-5" data-id="element-547" data-mp="1cw04t8kx3f9g">
              {others.slice(0, 3).map(article => <li key={article.slug} className="border-t border-midnight-900/15" data-id="element-548" data-mp="20m481eu71czx">
                  <Link to={`/insights/${article.slug}`} className="group block py-6" data-id="element-549" data-mp="25lyxddybluiv">
                    <p className="text-sm text-midnight-900/55" data-id="element-550" data-mp="1rlo2lztw3vsf">
                      {article.category} · {article.readTime}
                    </p>
                    <h3 className="mt-2 font-serif text-2xl leading-snug text-midnight-900 transition-colors duration-150 ease-out group-hover:text-midnight-600" data-id="element-551" data-mp="1wj7i132bqely">
                      {article.title}
                    </h3>
                  </Link>
                </li>)}
            </ul>
          </div>
        </div>
      </section>

      <CtaBand data-id="element-552" data-mp="2hvonb05y1uyi" />
    </>;
}